<script setup lang="ts">
import {
    nextTick, onBeforeUnmount, onMounted, ref,
    watch,
} from 'vue';

import { useMainMenuStateRef } from '#public_ts/composables';

import type { CbWidgetExposedApi } from './cb-widget-integration';

interface CbWidgetApi {
    open(): void;
    close(): void;
    toggle(): void;
    setLanguage(lang: string): void;
    setPosition(pos: string): void;
    setOffset(offsetX: string, offsetY: string): void;
    isOpen: boolean;
}

interface WidnowWithCBWidget extends Window {
    CBWidget?: CbWidgetApi;
    process?: { env: { NODE_ENV: string } };
}

const props = withDefaults(defineProps<{
    lang?: string;
    initiallyOpen?: boolean;
}>(), {
    lang: undefined,
    initiallyOpen: false,
});

const ready = ref(false);
let scriptEl: HTMLScriptElement | null = null;

function waitForReady(): Promise<CbWidgetApi> {
    return new Promise((resolve, reject) => {
        const win = window as unknown as WidnowWithCBWidget;
        if(win.CBWidget) {
            resolve(win.CBWidget);
            return;
        }
        let attempts = 0;
        const maxAttempts = 100;
        const interval = setInterval(() => {
            attempts++;
            if(win.CBWidget) {
                clearInterval(interval);
                resolve(win.CBWidget);
            } else if(attempts >= maxAttempts) {
                clearInterval(interval);
                reject(new Error('CBWidget failed to load'));
            }
        }, 100);
    });
}

function getApi(): CbWidgetApi | undefined {
    return (window as unknown as WidnowWithCBWidget).CBWidget;
}

function open() {
    getApi()?.open();
}

function close() {
    getApi()?.close();
}

function toggle() {
    getApi()?.toggle();
}

function setLanguage(lang: string) {
    getApi()?.setLanguage(lang);
}

function setPosition(pos: string) {
    getApi()?.setPosition(pos);
}

function setOffset(offsetX: string, offsetY: string) {
    getApi()?.setOffset(offsetX, offsetY);
}

function getIsOpen(): boolean {
    return getApi()?.isOpen ?? false;
}


defineExpose({
    ready,
    open,
    close,
    toggle,
    setLanguage,
    setPosition,
    setOffset,
    isOpen: getIsOpen,
} satisfies CbWidgetExposedApi);

const { isFattapp2Active } = useMainMenuStateRef();
let timeOut: number | undefined;

function initOffset() {
    const offset = isFattapp2Active.value ? '+6em' : '+2em';
    scriptEl?.setAttribute('data-offset-y', offset);
    clearTimeout(timeOut);
    timeOut = setTimeout(() => {
        setOffset('', offset);
    }, 500) as unknown as number;
}

watch(isFattapp2Active, initOffset);

function initWidget() {
    initOffset();
    if(props.initiallyOpen) {
        open();
    }
}

onMounted(() => {
    const apiUrl = 'https://elk.comped.it/cb';
    nextTick(() => {
        scriptEl = document.createElement('script');
        scriptEl.src = `${apiUrl.replace(/\/+$/, '')}/widget.js`;
        scriptEl.setAttribute('data-api-url', apiUrl);
        if(props.lang) {
            scriptEl.setAttribute('data-lang', props.lang);
        }
        scriptEl.setAttribute('data-position', 'bottom-right');
        initWidget();
        scriptEl.async = true;

        const win = window as unknown as WidnowWithCBWidget;
        if(!win.process) {
            win.process = { env: { NODE_ENV: 'production' } };
        }

        document.head.appendChild(scriptEl);

        waitForReady().then(() => {
            ready.value = true;
            initWidget();
        }).catch((err) => {
            console.error(err);
        });
    });
});

onBeforeUnmount(() => {
    if(scriptEl && scriptEl.parentNode) {
        scriptEl.parentNode.removeChild(scriptEl);
        scriptEl = null;
    }
    ready.value = false;
});
</script>

<template>
    <div v-once class="cb-widget-root" />
</template>
